<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { approvePost, listPendingPosts, rejectPost, type AdminPostItem } from '../api';

const loading = ref(false);
const submitting = ref(false);
const list = ref<AdminPostItem[]>([]);
const current = ref<AdminPostItem | null>(null);
let listSeq = 0;

async function reload() {
  const seq = ++listSeq;
  loading.value = true;
  list.value = [];
  current.value = null;
  try {
    const next = await listPendingPosts();
    if (seq !== listSeq) return;
    list.value = next;
  } catch (e) {
    if (seq === listSeq) ElMessage.error(normalizeError(e));
  } finally {
    if (seq === listSeq) loading.value = false;
  }
}

function openDetail(item: AdminPostItem) {
  current.value = item;
}

async function onApprove() {
  const item = current.value;
  if (!item || submitting.value) return;
  try {
    await ElMessageBox.confirm('确认复核通过?通过后帖子将进入正常展示(VISIBLE)。', '复核通过', {
      confirmButtonText: '通过',
      cancelButtonText: '取消',
      type: 'success',
    });
  } catch {
    return;
  }

  submitting.value = true;
  try {
    await approvePost(item.id);
    ElMessage.success('已通过,帖子恢复展示');
    removeItem(item.id);
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    submitting.value = false;
  }
}

async function onReject() {
  const item = current.value;
  if (!item || submitting.value) return;
  let reason = '';
  try {
    const { value } = await ElMessageBox.prompt('请填写驳回原因(可留空,记入审计)', '复核驳回', {
      inputType: 'textarea',
      inputPlaceholder: '如:含推广引流 / 违规联系方式 / 不实信息',
      confirmButtonText: '驳回并下架',
      cancelButtonText: '取消',
      inputValidator: () => true,
    });
    reason = (value || '').trim();
  } catch {
    return;
  }

  submitting.value = true;
  try {
    await rejectPost(item.id, reason || undefined);
    ElMessage.success('已驳回,帖子已下架(BLOCKED)');
    removeItem(item.id);
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    submitting.value = false;
  }
}

function removeItem(id: string) {
  list.value = list.value.filter((item) => item.id !== id);
  if (current.value?.id === id) current.value = null;
}

function normalizeError(e: unknown): string {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null | undefined): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

function safetyTagType(level: string): 'warning' | 'danger' | 'info' {
  if (level === 'REVIEW') return 'warning';
  if (level === 'BLOCK') return 'danger';
  return 'info';
}

function textPreview(text: string): string {
  return text.length > 46 ? `${text.slice(0, 46)}…` : text;
}

onMounted(() => {
  reload();
});
</script>

<template>
  <div class="audit-pane">
    <div class="pane-head">
      <div class="pane-title">机审待复核队列</div>
      <el-button size="small" :disabled="loading" @click="reload">刷新</el-button>
    </div>

    <div class="layout">
      <div class="list" v-loading="loading">
        <el-empty v-if="!list.length && !loading" description="暂无待复核帖子" />
        <div
          v-for="item of list"
          :key="item.id"
          class="list-item"
          :class="{ active: current?.id === item.id }"
          @click="openDetail(item)"
        >
          <div class="list-row">
            <span class="name">{{ item.author?.displayName || '未知作者' }}</span>
            <span class="report-status st-pending">待复核</span>
          </div>
          <div class="list-reason">{{ textPreview(item.text) }}</div>
          <div class="list-sub">
            <span v-if="item.media.length">🖼 {{ item.media.length }} 图</span>
            <span>{{ formatTime(item.createdAt) }}</span>
          </div>
        </div>
      </div>

      <div class="detail">
        <div v-if="!current" class="detail-empty">
          <el-empty description="选一条帖子查看详情" />
        </div>
        <template v-else>
          <div class="detail-header">
            <div class="title">
              {{ current.author?.displayName || '未知作者' }}
              <span class="report-status st-pending">机审待复核</span>
            </div>
            <div class="subtitle" v-if="current.author">
              {{ current.author.schoolName || '未填学校' }}
              <span v-if="current.author.enrollYear">· {{ current.author.enrollYear }}届</span>
              · 账户 {{ current.author.status }}
            </div>
            <div class="subtitle">帖子 ID <code>{{ current.id }}</code></div>
            <div class="subtitle">
              发布于 {{ formatTime(current.createdAt) }} · 作用域 {{ current.visibleScope }}
              · 点赞 {{ current.likeCount }} · 评论 {{ current.commentCount }}
            </div>
          </div>

          <div class="section">
            <div class="section-title">帖子正文</div>
            <div class="reason-box">{{ current.text }}</div>
          </div>

          <div class="section" v-if="current.media.length">
            <div class="section-title">媒体({{ current.media.length }})</div>
            <div class="media-grid">
              <div v-for="m of current.media" :key="m.id" class="media-cell">
                <a :href="m.url" target="_blank" rel="noreferrer">
                  <img :src="m.url" class="media-img" :alt="`媒体 ${m.sortOrder + 1}`" />
                </a>
                <el-tag size="small" :type="safetyTagType(m.safetyLevel)">
                  {{ m.safetyLevel }}
                </el-tag>
                <span v-if="m.safetyReason" class="media-reason">{{ m.safetyReason }}</span>
              </div>
            </div>
          </div>

          <div class="detail-actions">
            <el-button
              type="success"
              size="large"
              :loading="submitting"
              :disabled="submitting"
              @click="onApprove"
            >
              复核通过
            </el-button>
            <el-button
              type="danger"
              size="large"
              :loading="submitting"
              :disabled="submitting"
              @click="onReject"
            >
              驳回并下架
            </el-button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.audit-pane { display: flex; flex-direction: column; height: calc(100vh - 80px); }
.pane-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.pane-title { font-size: 15px; font-weight: 600; color: #1d1d1f; }
.layout { display: flex; flex: 1; gap: 16px; overflow: hidden; }
.list { width: 360px; overflow-y: auto; background: #fff; border-radius: 8px; padding: 8px; }
.list-item { padding: 12px; border-radius: 6px; cursor: pointer; margin-bottom: 6px; border: 1px solid transparent; }
.list-item:hover { background: #f5f5f7; }
.list-item.active { background: #e6f4ff; border-color: #409eff; }
.list-row { display: flex; justify-content: space-between; align-items: center; }
.name { font-weight: 600; }
.list-reason { margin-top: 8px; color: #1d1d1f; line-height: 1.45; font-size: 14px; }
.list-sub { margin-top: 6px; font-size: 12px; color: #86868b; display: flex; gap: 10px; }

.report-status { font-size: 12px; padding: 1px 8px; border-radius: 10px; font-weight: 500; }
.st-pending { background: #fff4e0; color: #b26a00; }

.detail { flex: 1; overflow-y: auto; background: #fff; border-radius: 8px; padding: 24px; }
.detail-empty { height: 100%; display: flex; align-items: center; justify-content: center; }
.detail-header .title { font-size: 22px; font-weight: 600; display: flex; align-items: center; gap: 10px; }
.detail-header .subtitle { margin-top: 6px; color: #666; font-size: 13px; }
.detail-header .subtitle code { background: #f5f5f7; padding: 2px 6px; border-radius: 4px; font-size: 12px; }

.section { margin-top: 24px; }
.section-title { font-size: 14px; color: #86868b; margin-bottom: 6px; }
.reason-box {
  background: #f5f5f7;
  border-radius: 6px;
  padding: 16px;
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 14px;
}
.media-grid { display: flex; flex-wrap: wrap; gap: 16px; }
.media-cell { display: flex; flex-direction: column; gap: 6px; width: 160px; }
.media-img { width: 160px; height: 160px; object-fit: cover; border-radius: 6px; border: 1px solid #e4e7ed; cursor: zoom-in; }
.media-reason { font-size: 12px; color: #b42020; }

.detail-actions { margin-top: 32px; display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
</style>
